/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { pageTypes } from '@/constants/page';
import { useBrandProductData } from '@/features/brands/api/getProductBrandData';
import { useLocale } from '@/hooks/useLocale';
import { ResultsList } from '@/features/shelf/components/PageTemplates/Shelf/ResultsList';
import type { ShelfPart } from '@/types/legacy';
import { usePageType } from '@/hooks/usePageType';
import { useProductShelfResults } from '@/features/category/api/getProductShelfResults';
import { persistXFusionQueryId } from '@/utils/xFusionQueryIdHelpers';
import { useSearchProductData } from '@/features/search/api/getSearchProductData';
import { useProductShelfACESResults } from '@/features/category/api/getProductShelfACESResults';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { useACESBrandProductData } from '@/features/brands/api/getAcesProductBrandData';
import { useMerchShelfResults } from '@/features/category/api/getMerchShelfResults';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';

type Props = {
  resultsListRef?: React.RefObject<HTMLDivElement | null>;
  handleDealClick: (
    part: ShelfPart,
    price: string,
    quantity: number,
    ref: React.RefObject<HTMLButtonElement | null>,
    fulfillmentTypeId: number | null,
  ) => void;
};

export const CMSResultList = ({ resultsListRef, handleDealClick }: Props) => {
  const router = useLegacyRouter();
  const locale = useLocale();
  const { pageType } = usePageType();
  const { acesEnabled } = useAcesFlags();
  const isBrandShelf = pageType === pageTypes.BrandShelf;
  const isSearchResults = pageType === pageTypes.search;
  const isMerchShelfPage =
    router.route === '/deals/[cmsPath]/[[...slug]]' || pageType === pageTypes.MerchShelf;

  const useBrandProductDataFunction = acesEnabled ? useACESBrandProductData : useBrandProductData;
  const { data: brandProductData } = useBrandProductDataFunction({ enabled: isBrandShelf });

  const { data: searchProductData } = useSearchProductData({ enabled: isSearchResults });

  const useShelfResultsFunction = acesEnabled ? useProductShelfACESResults : useProductShelfResults;
  const { data: productShelfResultsData } = useShelfResultsFunction();

  const { data: merchShelfResultsData } = useMerchShelfResults();

  let xFusionQueryId;
  if (isSearchResults) {
    xFusionQueryId = searchProductData?.xFusionQueryId;
  } else if (isBrandShelf) {
    xFusionQueryId = brandProductData?.xFusionQueryId;
  } else if (isMerchShelfPage && acesEnabled) {
    xFusionQueryId = merchShelfResultsData?.xfusionQueryId;
  } else {
    xFusionQueryId = productShelfResultsData?.xFusionQueryId;
  }
  persistXFusionQueryId(xFusionQueryId);

  return (
    <ResultsList
      locale={locale}
      handleDealClick={handleDealClick}
      xFusionQueryId={xFusionQueryId}
      resultsListRef={resultsListRef}
    />
  );
};
